/**
 * REVCLOUD entity → `RenderableEntity` adapter (PR-D, LEGACY string-bag path).
 *
 * AutoCAD's REVCLOUD is a closed polyline whose every segment is replaced by a
 * half-circle arc bulge (giving the cloud-bubble look). The drawing-model
 * carries the underlying polyline vertices plus an `approximateArcLength` value
 * that drives bulge curvature. Our 3D renderer feeds the resulting arc chain
 * through the line pipeline (existing batchedLayer path), so this adapter emits
 * a `kind: "polyline"` SceneGeometry whose vertices already trace the cloud
 * curve.
 *
 * # Why polyline kind
 *
 * `SceneGeometry` does not (yet) have a `kind: "revcloud"` discriminator
 * (PR-A type definitions). The plan deliberately maps revcloud onto the line
 * pipeline using a tessellated polyline so the BatchedLayer can pack its
 * segments alongside ordinary lines without a separate mesh class. Each arc in
 * the cloud chain is sampled at the same density as the SVG renderer's
 * `describeRevisionCloud` helper.
 *
 * # Vertex source
 *
 * `buildRevisionCloudPreview` already emits Corner 1/2 X/Y for the existing SVG
 * path AND `__revcloud_vertices` (JSON-encoded `RevisionCloudEntity.vertices`).
 * This adapter prefers the JSON list because it preserves polygonal/freehand
 * revcloud shapes; if it is missing it falls back to the rectangular Corner 1/2
 * envelope.
 *
 * # DEFUSE Direction-3
 *
 * The cloud-bulge tessellation now lives in the shared pure builder
 * `geom/revisionCloudGeometry.ts`, called by BOTH this legacy bag adapter and
 * the typed `typed/revisionCloudFromEntity.ts`. This file is reduced to its
 * value extraction from the `EntityPreview.properties` string bag (raw,
 * PRE-offset vertices + `closed` + arc length) + a single builder call;
 * `applyOffset` is applied INSIDE the builder, so the parsing below reads raw
 * world-space x/y (z dropped to 0, as the bag never carries vertex z) and lets
 * the builder do the offset — behaviour-preserving vs. the prior inline math.
 *
 * # Edge cases
 *
 *   - 0 vertices → fall back to corner rectangle.
 *   - Closed flag = false → arcs do not include the closing edge.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber } from "../../scene/renderHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import { buildRenderableShell, type AdapterContext } from "./adapterContext";
import { buildRevisionCloudSceneGeometry } from "./geom/revisionCloudGeometry";

export function revisionCloudToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity | null {
  const baseVertices = readVertices(entity);
  if (baseVertices.length < 2) return null;

  const closed =
    String(entity.properties.Closed ?? "true").toLowerCase() === "true";
  const arcLength = readNumber(
    entity.properties,
    "Approximate arc length",
    15,
  );

  // Single one-truth tessellation (offset applied INSIDE the builder).
  const result = buildRevisionCloudSceneGeometry(baseVertices, closed, arcLength);
  if (!result) return null;

  // Tag with the original entity type via the shell — buildRenderableShell
  // stores entity.type, and EntityPreview.type stays "Revision Cloud" so
  // downstream consumers (selection labels, properties panel) keep their
  // AutoCAD-correct identity.
  return buildRenderableShell(entity, ctx, result.geometry, result.bounds);
}

/**
 * Parse the raw (PRE-offset) revcloud vertices from the property bag. Prefers
 * the JSON `__revcloud_vertices` list (preserves polygonal/freehand shapes);
 * falls back to the rectangular Corner 1/2 envelope. z is always 0 (the bag
 * never carries vertex z). `applyOffset` is NOT applied here — the shared
 * builder applies it, so these are raw world-space points.
 */
function readVertices(entity: EntityPreview): Vec3[] {
  const raw = entity.properties.__revcloud_vertices;
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const out: Vec3[] = [];
        for (const v of parsed) {
          if (typeof v !== "object" || v === null) continue;
          const obj = v as { x?: unknown; y?: unknown };
          const x = Number(obj.x);
          const y = Number(obj.y);
          if (Number.isFinite(x) && Number.isFinite(y)) {
            out.push({ x, y, z: 0 });
          }
        }
        if (out.length >= 2) return out;
      }
    } catch {
      /* fall through */
    }
  }
  // Fallback: rectangular Corner 1 / Corner 2 envelope mirrors the existing SVG
  // describeRevisionCloud rectangle.
  const x1 = readNumber(entity.properties, "Corner 1 X");
  const y1 = readNumber(entity.properties, "Corner 1 Y");
  const x2 = readNumber(entity.properties, "Corner 2 X");
  const y2 = readNumber(entity.properties, "Corner 2 Y");
  if (
    !Number.isFinite(x1) ||
    !Number.isFinite(y1) ||
    !Number.isFinite(x2) ||
    !Number.isFinite(y2)
  ) {
    return [];
  }
  return [
    { x: Math.min(x1, x2), y: Math.min(y1, y2), z: 0 },
    { x: Math.max(x1, x2), y: Math.min(y1, y2), z: 0 },
    { x: Math.max(x1, x2), y: Math.max(y1, y2), z: 0 },
    { x: Math.min(x1, x2), y: Math.max(y1, y2), z: 0 },
  ];
}
